import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Finance({ user }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [salaryBands, setSalaryBands] = useState([]);
  const [payrollCycles, setPayrollCycles] = useState([]);
  const [clientDeposits, setClientDeposits] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasAccess, setHasAccess] = useState(false);

  const [depositForm, setDepositForm] = useState({
    site_id: '',
    client_name: '',
    deposit_amount: '',
    notes: '',
  });

  const [salaryForm, setSalaryForm] = useState({
    rank_id: '',
    base_salary: '',
    effective_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    checkAccess();
  }, [user]);

  const checkAccess = () => {
    // Check if user has Finance role or is Command level
    const hasFinanceRole = user.roles?.some(r => r.name === 'Finance');
    const isCommand = user.rank?.level === 5;
    setHasAccess(hasFinanceRole || isCommand);
    if (hasFinanceRole || isCommand) {
      fetchData();
    } else {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [salaryRes, cyclesRes, depositsRes, ranksRes, sitesRes] = await Promise.all([
        axios.get(`${API}/finance/salary-bands`),
        axios.get(`${API}/finance/payroll-cycles`),
        axios.get(`${API}/finance/client-deposits`),
        axios.get(`${API}/ranks`),
        axios.get(`${API}/sites`),
      ]);
      setSalaryBands(salaryRes.data);
      setPayrollCycles(cyclesRes.data);
      setClientDeposits(depositsRes.data);
      setRanks(ranksRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        setError(error.response?.data?.detail || 'Failed to load finance data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeposit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/finance/client-deposits`, {
        ...depositForm,
        deposit_amount: parseFloat(depositForm.deposit_amount),
      });
      setSuccess('Client deposit recorded successfully!');
      setShowDepositModal(false);
      setDepositForm({ site_id: '', client_name: '', deposit_amount: '', notes: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record deposit');
    }
  };

  const handleCreateSalaryBand = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/finance/salary-bands`, {
        ...salaryForm,
        base_salary: parseFloat(salaryForm.base_salary),
      });
      setSuccess('Salary band created successfully!');
      setShowSalaryModal(false);
      setSalaryForm({ rank_id: '', base_salary: '', effective_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create salary band');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="finance-restricted">
        <div className="military-card p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-600/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Restricted</h2>
          <p className="text-slate-400 mb-4">
            This module is restricted to <span className="text-amber-400 font-semibold">Finance Corps</span> personnel only.
          </p>
          <p className="text-slate-500 text-sm">
            Contact your commanding officer for access authorization.
          </p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalDeposits = clientDeposits.reduce((sum, d) => sum + (d.deposit_amount || 0), 0);
  const totalBalance = clientDeposits.reduce((sum, d) => sum + (d.credit_balance || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="finance-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-white">Finance & Compensation</h1>
            <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded border border-amber-600">
              GFAC
            </span>
          </div>
          <p className="text-slate-400">Guardian Finance Administration Corps - Restricted Access</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowSalaryModal(true)}
            className="btn-military flex items-center gap-2"
            data-testid="salary-band-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Salary Band
          </button>
          <button
            onClick={() => setShowDepositModal(true)}
            className="btn-gold flex items-center gap-2"
            data-testid="record-deposit-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Record Deposit
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Deposits</p>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalDeposits)}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Credit Balance</p>
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(totalBalance)}</p>
            </div>
            <div className="w-10 h-10 bg-amber-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Salary Bands</p>
              <p className="text-2xl font-bold text-white">{salaryBands.length}</p>
            </div>
            <div className="w-10 h-10 bg-blue-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>
        <div className="military-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Payroll Cycles</p>
              <p className="text-2xl font-bold text-white">{payrollCycles.length}</p>
            </div>
            <div className="w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav">
        <button
          onClick={() => setActiveTab('overview')}
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('salary')}
          className={`tab-button ${activeTab === 'salary' ? 'active' : ''}`}
          data-testid="tab-salary"
        >
          Salary Bands
        </button>
        <button
          onClick={() => setActiveTab('deposits')}
          className={`tab-button ${activeTab === 'deposits' ? 'active' : ''}`}
          data-testid="tab-deposits"
        >
          Client Deposits
        </button>
        <button
          onClick={() => setActiveTab('payroll')}
          className={`tab-button ${activeTab === 'payroll' ? 'active' : ''}`}
          data-testid="tab-payroll"
        >
          Payroll Cycles
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Salary Bands Summary */}
          <div className="military-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Salary Structure by Rank</h3>
            <div className="space-y-3">
              {salaryBands.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No salary bands configured</p>
              ) : (
                salaryBands.map((band) => {
                  const rank = ranks.find(r => r.id === band.rank_id);
                  return (
                    <div key={band.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-300 font-medium">{rank?.name || 'Unknown'}</span>
                      <span className="text-emerald-400 font-bold">{formatCurrency(band.base_salary)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Recent Deposits */}
          <div className="military-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Recent Client Deposits</h3>
            <div className="space-y-3">
              {clientDeposits.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No deposits recorded</p>
              ) : (
                clientDeposits.slice(0, 5).map((deposit) => {
                  const site = sites.find(s => s.id === deposit.site_id);
                  return (
                    <div key={deposit.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{deposit.client_name}</p>
                        <p className="text-slate-500 text-sm">{site?.site_name || 'Unknown Site'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">{formatCurrency(deposit.deposit_amount)}</p>
                        <p className="text-slate-500 text-sm">Balance: {formatCurrency(deposit.credit_balance)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Salary Bands Tab */}
      {activeTab === 'salary' && (
        <div className="military-card overflow-hidden">
          <table className="military-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Base Salary</th>
                <th>Effective Date</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {salaryBands.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-slate-500">
                    No salary bands configured
                  </td>
                </tr>
              ) : (
                salaryBands.map((band) => {
                  const rank = ranks.find(r => r.id === band.rank_id);
                  return (
                    <tr key={band.id} data-testid={`salary-band-${band.id}`}>
                      <td className="text-white font-medium">{rank?.name || 'Unknown'}</td>
                      <td className="text-emerald-400 font-bold">{formatCurrency(band.base_salary)}</td>
                      <td>{band.effective_date}</td>
                      <td>{band.currency || 'USD'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Deposits Tab */}
      {activeTab === 'deposits' && (
        <div className="military-card overflow-hidden">
          <table className="military-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Site</th>
                <th>Deposit Amount</th>
                <th>Credit Balance</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {clientDeposits.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-slate-500">
                    No deposits recorded
                  </td>
                </tr>
              ) : (
                clientDeposits.map((deposit) => {
                  const site = sites.find(s => s.id === deposit.site_id);
                  return (
                    <tr key={deposit.id} data-testid={`deposit-${deposit.id}`}>
                      <td className="text-white font-medium">{deposit.client_name}</td>
                      <td>{site?.site_name || 'Unknown'}</td>
                      <td className="text-emerald-400 font-bold">{formatCurrency(deposit.deposit_amount)}</td>
                      <td className="text-amber-400">{formatCurrency(deposit.credit_balance)}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          deposit.status === 'Active' ? 'bg-emerald-900/50 text-emerald-300' :
                          deposit.status === 'Depleted' ? 'bg-red-900/50 text-red-300' :
                          'bg-slate-900/50 text-slate-300'
                        }`}>
                          {deposit.status}
                        </span>
                      </td>
                      <td>{new Date(deposit.deposit_date).toLocaleDateString()}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Payroll Cycles Tab */}
      {activeTab === 'payroll' && (
        <div className="military-card overflow-hidden">
          <table className="military-table">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>Status</th>
                <th>Total Payout</th>
                <th>Processed Date</th>
              </tr>
            </thead>
            <tbody>
              {payrollCycles.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-slate-500">
                    No payroll cycles found
                  </td>
                </tr>
              ) : (
                payrollCycles.map((cycle) => (
                  <tr key={cycle.id} data-testid={`payroll-${cycle.id}`}>
                    <td className="text-white font-medium">{cycle.cycle_month} {cycle.cycle_year}</td>
                    <td>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        cycle.status === 'Completed' ? 'bg-emerald-900/50 text-emerald-300' :
                        cycle.status === 'Processing' ? 'bg-amber-900/50 text-amber-300' :
                        'bg-slate-900/50 text-slate-300'
                      }`}>
                        {cycle.status}
                      </span>
                    </td>
                    <td className="text-emerald-400 font-bold">{formatCurrency(cycle.total_payout)}</td>
                    <td>{cycle.processed_date || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Deposit Modal */}
      {showDepositModal && (
        <div className="modal-overlay" data-testid="deposit-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Record Client Deposit</h2>
              <button onClick={() => setShowDepositModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateDeposit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Site</label>
                <select
                  value={depositForm.site_id}
                  onChange={(e) => setDepositForm({ ...depositForm, site_id: e.target.value })}
                  required
                  className="input-military"
                  data-testid="deposit-site-select"
                >
                  <option value="">Select Site</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>{site.site_name} ({site.site_code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Client Name</label>
                <input
                  type="text"
                  value={depositForm.client_name}
                  onChange={(e) => setDepositForm({ ...depositForm, client_name: e.target.value })}
                  required
                  className="input-military"
                  data-testid="deposit-client-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Deposit Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={depositForm.deposit_amount}
                  onChange={(e) => setDepositForm({ ...depositForm, deposit_amount: e.target.value })}
                  required
                  className="input-military"
                  placeholder="0.00"
                  data-testid="deposit-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Notes</label>
                <textarea
                  value={depositForm.notes}
                  onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
                  className="input-military h-20 resize-none"
                  placeholder="Optional notes..."
                  data-testid="deposit-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowDepositModal(false)} className="btn-military">
                  Cancel
                </button>
                <button type="submit" className="btn-gold" data-testid="submit-deposit">
                  Record Deposit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Salary Band Modal */}
      {showSalaryModal && (
        <div className="modal-overlay" data-testid="salary-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Create Salary Band</h2>
              <button onClick={() => setShowSalaryModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateSalaryBand} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Rank</label>
                <select
                  value={salaryForm.rank_id}
                  onChange={(e) => setSalaryForm({ ...salaryForm, rank_id: e.target.value })}
                  required
                  className="input-military"
                  data-testid="salary-rank-select"
                >
                  <option value="">Select Rank</option>
                  {ranks.map((rank) => (
                    <option key={rank.id} value={rank.id}>{rank.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Base Monthly Salary ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={salaryForm.base_salary}
                  onChange={(e) => setSalaryForm({ ...salaryForm, base_salary: e.target.value })}
                  required
                  className="input-military"
                  placeholder="0.00"
                  data-testid="salary-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Effective Date</label>
                <input
                  type="date"
                  value={salaryForm.effective_date}
                  onChange={(e) => setSalaryForm({ ...salaryForm, effective_date: e.target.value })}
                  required
                  className="input-military"
                  data-testid="salary-date-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowSalaryModal(false)} className="btn-military">
                  Cancel
                </button>
                <button type="submit" className="btn-gold" data-testid="submit-salary">
                  Create Salary Band
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Finance;
